import classNames from "classnames";
import { uniqWith } from "lodash";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocalStorage } from "react-use";

import { Specimen } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import { showMask } from "shared/actions/slideDetailActions";
import { setInputMacroCode } from "shared/actions/specimenDetailActions";
import { LesionLine, lesionLineTypeByName } from "shared/reducers/aiReducer";
import {
  macroCodesWithSubtypePossibilities,
  PositiveLesionLine,
} from "shared/utils/aiLinesAndSubtypes";
import { caseSlideLabel } from "shared/utils/slideUtils";

import { selectSpecimenClassification } from "selectors/classificationSelector";
import { useAppSelector } from "store/hooks";

export const alternateMacroCodes = {
  WDSCC: ["SCCKA", "SUPSCC"],
  SCCIS: ["AK", "SCCISAE"],
  AK: ["SCCIS", "HAK"],
  BCCS: ["BCCSN", "BCCN"],
  BCCN: ["BCCSN", "BCCNI"],
  BCCI: ["BCCN", "BCCNI"],
  BCCSN: ["BCCN", "BCCS"],
  BCCNI: ["BCCN", "BCCI"],
  BCCSNI: ["BCCNI", "BCCSI"],
  SKII: ["SKVV", "SKP"],
  VVII: ["VVIR", "SKVV"],
  PC: ["PCR", "EIC"],
  EIC: ["EICR", "MIL"],
};

export const mergeAlternateMacroCodes = (aicodes: string[] | undefined) => {
  if (!aicodes) return [];
  const aicodesUC = [...aicodes.map((v) => v.toUpperCase())];

  const merged = aicodesUC.reduce((acc: string[], code) => {
    const alternateCodes =
      alternateMacroCodes[code as keyof typeof alternateMacroCodes];
    if (alternateCodes) {
      acc.push(...alternateCodes);
    }
    return acc;
  }, []);

  return [...new Set(merged)].filter((v) => !aicodesUC.includes(v));
};

type DiagnosisDialogAIProps = {
  currentSpecimen?: Specimen;
  currentDiagnosis: CaseDiagnosis;
  prospectiveMacroCode: string;
  updateDiagnosis: Function;
  positiveLesionLines?: LesionLine[];
  lesionLine?: LesionLine;
  setProspectiveMacroCode?: React.Dispatch<React.SetStateAction<string>>;
  macroCodeInputRef?: React.RefObject<HTMLInputElement>;
};
const DiagnosisDialogAI = ({
  positiveLesionLines,
  currentDiagnosis,
  currentSpecimen,
  prospectiveMacroCode,
  setProspectiveMacroCode,
  macroCodeInputRef,
}: DiagnosisDialogAIProps) => {
  const dispatch = useDispatch();

  const caseDetail = useAppSelector(({ caseDetail }) => caseDetail);

  const specimenClassification = useAppSelector((state) =>
    selectSpecimenClassification(state, currentSpecimen?.id)
  );

  const inputMacroCode = useAppSelector(
    (state) =>
      state.caseDetail.specimenDetail.find((s) => s.id === currentSpecimen?.id)
        ?.inputMacroCode
  );

  const positiveLines: PositiveLesionLine[] = useMemo(() => {
    return [
      ...new Set(positiveLesionLines?.map((ll) => ll.diagnosis_name)),
    ].map((diagnosis_name) => {
      const classificationesWithLines =
        specimenClassification?.filter((s) =>
          s?.lesion_lines?.some(
            (ll) =>
              ll.diagnosis_name === diagnosis_name &&
              ll.diagnosis_result === "POSITIVE"
          )
        ) || [];

      const linesOfName = classificationesWithLines
        .flatMap((c) => c?.lesion_lines || [])
        .filter(
          (ll) =>
            ll.diagnosis_name === diagnosis_name &&
            ll.diagnosis_result === "POSITIVE"
        )
        .filter((v) => v);

      return {
        diagnosis_name,
        linesOfName,
        subtypes: [
          ...new Set(
            positiveLesionLines
              ?.filter((ll) => ll.diagnosis_name === diagnosis_name)
              .flatMap((ll) =>
                ll.subtypes?.filter((s) =>
                  ["POSITIVE", "INCONCLUSIVE"].includes(s.diagnosis_result)
                )
              )
              // .sort((a, b) => a.severity - b.severity) TODO: sort subtype by severity? how does this work elsewhere?
              .map(
                (s) =>
                  `${s?.diagnosis}${
                    s?.diagnosis_result === "INCONCLUSIVE"
                      ? " (Inconclusive)"
                      : ""
                  }`
              )
          ),
        ],
        slide_ids: classificationesWithLines.map((s) => s?.slide_id),
      };
    });
  }, [positiveLesionLines, specimenClassification]);

  // Swapped to lodash uniqWith due to swap to objects with positive line and code instead of just codes
  const allDxcodes = [
    ...uniqWith(
      positiveLines.flatMap((p) =>
        p.linesOfName.flatMap((l) => macroCodesWithSubtypePossibilities(p, l))
      ),
      (a, b) => a?.code === b?.code
    ),
  ].filter((v) => v);

  // Set the input and overlay if macro code is selected
  const onExecuteMacroCode = (
    positiveLine: (typeof positiveLines)[0],
    dxcode: string
  ) => {
    if (currentSpecimen?.id) {
      dispatch(setInputMacroCode(currentSpecimen?.id, dxcode));
    }
    dispatch(
      showMask(lesionLineTypeByName(positiveLine?.diagnosis_name || ""))
    );
    if (macroCodeInputRef?.current) {
      macroCodeInputRef.current.focus();
    }
  };

  // Ported from DiagnosisDialog.tsx, probably better belongs here
  // Sets the input if only 1 macro code exists
  useEffect(() => {
    if (
      allDxcodes.length === 1 &&
      currentSpecimen?.id &&
      !currentDiagnosis.macro_code
    ) {
      onExecuteMacroCode(allDxcodes.at(0)!.line, allDxcodes.at(0)!.code);
    }
  }, [currentSpecimen?.id]);

  // Hack to fix widths on the subtypes
  const [commonWidth] = useLocalStorage<number | undefined>(
    "DIAGNOSIS_TEXTAREA_WIDTH"
  );
  // If it's under 400, bump it to 400
  const subtypeMaxWidth = commonWidth
    ? commonWidth < 400
      ? 400
      : commonWidth
    : undefined;

  return (
    <div>
      {(!positiveLesionLines || positiveLesionLines?.length < 1) && (
        <div className="Sidebar__AI">
          <p>
            <strong>No Positive Lesion Lines</strong>
          </p>
        </div>
      )}

      {positiveLines.map((p) => {
        const dxcodes: string[] = [
          ...new Set(
            p.linesOfName
              .map((line) => macroCodesWithSubtypePossibilities(p, line)?.code)
              .filter((v) => v) as string[]
          ),
        ].filter((v) => v);
        const slides = caseDetail.slides?.filter((s) =>
          p.slide_ids.includes(s.id)
        );
        const margins = [
          ...new Set(p.linesOfName.map((l) => l.margin_status)),
        ].filter((v) => v);
        const alternateDxcodes = mergeAlternateMacroCodes(dxcodes as string[]);

        return (
          <div className="Sidebar__AI">
            <div>
              <div className="Sidebar__AI__diagnosis">
                <p>
                  <span className="Sidebar__AI__name">
                    {p.diagnosis_name}
                    {!!slides?.length && (
                      <span>
                        {" "}
                        &mdash;{slides.length > 2 ? <br /> : ""}
                        <span
                          className={classNames({
                            "Sidebar__AI__slides--long": slides.length > 2,
                          })}
                          style={{ maxWidth: subtypeMaxWidth }}
                        >
                          {slides
                            .map((s) => caseSlideLabel(s, caseDetail))
                            .join(", ")}
                        </span>
                      </span>
                    )}
                  </span>
                  <span
                    className="LesionLineType__subtypes"
                    style={{ maxWidth: subtypeMaxWidth }}
                  >
                    <span className="LesionLineType__legend">
                      {p.subtypes.join(", ")}
                      {/* TODO: separate conclusive, inconclusive */}
                    </span>
                    {!!margins.length && (
                      <>
                        {" "}
                        &mdash;{" "}
                        <code>
                          {margins.join(", ")} Margin
                          {margins.length > 1 && <>s</>}
                        </code>
                      </>
                    )}
                  </span>
                  {/* <SubtypesAndMargin lesionLine={diagnosableLesionLine} /> */}
                  {/* positive margins [only], positive & negative margins, negative margins [only] */}
                </p>
              </div>
              {dxcodes.map((dxcode) => (
                <button
                  key={dxcode}
                  className={classNames("Sidebar__AI__macro", {
                    "Sidebar__AI__macro--selected": dxcode === inputMacroCode,
                  })}
                  title={dxcode}
                  onClick={() => {
                    onExecuteMacroCode(p, dxcode);
                  }}
                  onFocus={
                    setProspectiveMacroCode
                      ? () => setProspectiveMacroCode(dxcode)
                      : undefined
                  }
                  onBlur={
                    setProspectiveMacroCode
                      ? () => setProspectiveMacroCode("")
                      : undefined
                  }
                >
                  <img src="/pw-white.png" alt="" />
                  <span>{dxcode}</span>
                </button>
              ))}
              {alternateDxcodes?.map((altCode) => (
                <button
                  key={altCode}
                  className={classNames(
                    "Sidebar__AI__macro Sidebar__AI__macro--alternate",
                    {
                      "Sidebar__AI__macro--selected":
                        altCode === inputMacroCode,
                    }
                  )}
                  title={`${altCode} (alternate)`}
                  onClick={() => {
                    onExecuteMacroCode(p, altCode);
                  }}
                  onFocus={
                    setProspectiveMacroCode
                      ? () => setProspectiveMacroCode(altCode)
                      : undefined
                  }
                  onBlur={
                    setProspectiveMacroCode
                      ? () => setProspectiveMacroCode("")
                      : undefined
                  }
                >
                  {altCode}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DiagnosisDialogAI;
